import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Typography, Button, Dropdown, Menu, Row, Spin, Col } from "antd";
import { DocumentFileRow, journalsQueries } from "@/entities/journal";
import { RepresentativeModalOrgs } from "@/features/representation-orgs-modal";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator, useDisclosure } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { fallbackString, formatAdministrativeDocument } from "@/shared/utils";
import { $journalApi } from "@entities/journal/fetch/fetch-client.ts";
import { useTitleAmendmentMode } from "../../../../model";
import { RepresentativesSignatureWrapper } from "../../../representatives-signature-wrapper";

export function BuildingContractorRepresentative() {
  const { titleId } = useCoordinator();
  const { isFetching } = useQuery(journalsQueries.getTitleJournal({ titleId }));
  const { data } = useQuery(journalsQueries.getTitleJournal({ titleId }));
  const titleStatus =
    data?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !data?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;
  const [key, setKey] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const buildingContractor =
    data?.mainJournal?.titleJournal?.buildingContractor;

  const { mutate: removeParticipant, isPending } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/title/{titleId}/representatives",
    {
      body: (data: any) => data,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
      },
    },
  );

  const handleDelete = (id: string) => {
    removeParticipant({
      params: {
        path: { titleId },
      },
      body: {
        buildingContractor: {
          projectParticipantId: buildingContractor?.projectParticipantId,
          representatives: [{ representativeId: id }],
        },
      },
    });
  };

  const handleClose = () => {
    onClose();
    setKey((prev) => prev + 1);
  };

  const representatives = buildingContractor?.representatives || [];

  const menu = (id: string) => (
    <Menu>
      <Menu.Item
        key="delete"
        icon={<BasketIcon />}
        onClick={() => handleDelete(id)}
      >
        Удалить
      </Menu.Item>
    </Menu>
  );

  return (
    <Spin spinning={isFetching || isPending}>
      <div>
        {canAddData ? (
          <div className="journal-title-block-header journal-title-block-header--spaced">
            <div className="journal-title-section-heading">
              Уполномоченный представитель лица, осуществляющего строительство
            </div>

            <Can I="edit" a="title_journals">
              <Button
                type={representatives?.length > 0 ? "default" : "primary"}
                onClick={onOpen}
                disabled={
                  !buildingContractor?.projectParticipantId ||
                  isFetching ||
                  isPending
                }
                style={{
                  // @ts-expect-error backgroundColor может быть boolean из-за short-circuit
                  backgroundColor:
                    representatives?.length > 0 && "rgba(234, 238, 248, 1)",
                }}
              >
                Добавить сотрудника
              </Button>
            </Can>
            <RepresentativeModalOrgs
              key={key}
              isOpen={isOpen}
              handleClose={handleClose}
              organization={{
                representativeType: "buildingContractor",
                isConstructionControl: false,
                companyId: buildingContractor?.company?.companyId ?? "",
                projectParticipantId:
                  buildingContractor?.projectParticipantId ?? "",
                userCompaniesHistoryId:
                  buildingContractor?.company?.companyHistoryId ?? "",
              }}
              // @ts-expect-error existingRepresentatives не совпадает с openapi типом
              existingRepresentatives={representatives}
              enableMultiAdd
              isAddFromOldService
            />
          </div>
        ) : null}
        {representatives.map((rep) => (
          <RepresentativesSignatureWrapper
            representativeType="buildingContractor"
            representative={rep}
          >
            <div key={rep.representativeId} className="journal-title-rep-card">
              <div className="journal-title-rep-card__header journal-title-rep-card__header--row">
                <Typography.Title
                  level={5}
                  className="journal-title-rep-card__name"
                >
                  {fallbackString(
                    `${rep.user?.surname || ""} ${rep.user?.firstName || ""} ${rep.user?.middleName || ""}`.trim(),
                  )}
                </Typography.Title>
                {titleStatus && !isNewAmendment ? (
                  <Can I="edit" a="title_journals">
                    <Dropdown
                      overlay={menu(String(rep.representativeId))}
                      trigger={["click"]}
                    >
                      <div className="journal-title-menu-trigger">
                        <MoreOneIcon width={24} height={24} />
                      </div>
                    </Dropdown>
                  </Can>
                ) : null}
              </div>

              <Row gutter={[16, 16]} className="journal-title-rep-grid">
                <Col span={8} style={{ color: "#888888" }}>
                  Организация
                </Col>
                <Col span={16}>
                  {fallbackString(rep.administrativeDocument?.organization)}
                </Col>

                <Col span={8} style={{ color: "#888888" }}>
                  Номер в НОСТРОЙ
                </Col>
                <Col span={16}>
                  {fallbackString(rep.user?.specialistNumberBuilding)}
                </Col>

                <Col span={8} style={{ color: "#888888" }}>
                  Номер в НОПРИЗ
                </Col>
                <Col span={16}>
                  {fallbackString(rep.user?.specialistNumberEngineering)}
                </Col>

                <Col span={8} style={{ color: "#888888" }}>
                  Телефон
                </Col>
                <Col span={16}>{fallbackString(rep.user?.mobilePhone)}</Col>

                <Col span={8} style={{ color: "#888888" }}>
                  Почта
                </Col>
                <Col span={16}>
                  <a href={`mailto:${rep.user?.userEmail}`}>
                    {fallbackString(rep.user?.userEmail)}
                  </a>
                </Col>

                <Col span={8} style={{ color: "#888888" }}>
                  Документ, №, дата
                </Col>
                <Col span={16} className="journal-title-rep-doc-cell">
                  {rep.administrativeDocument?.administrativeDocumentId &&
                  rep.administrativeDocument?.documentUrl ? (
                    <DocumentFileRow
                      inline
                      variant="link"
                      downloadIconPosition="right"
                      fileName={
                        formatAdministrativeDocument(
                          rep.administrativeDocument,
                        ) ||
                        rep.administrativeDocument.fileName ||
                        "document.pdf"
                      }
                      documentUrl={rep.administrativeDocument.documentUrl}
                    />
                  ) : (
                    fallbackString(
                      formatAdministrativeDocument(rep.administrativeDocument),
                    )
                  )}
                </Col>
              </Row>
            </div>
          </RepresentativesSignatureWrapper>
        ))}
      </div>
    </Spin>
  );
}
